/**
 * How far a grounded ball actually goes — the SIM-side source of truth.
 *
 * ── Why this file exists, and why it moved here ─────────────────────────────
 *
 * This model was written for the AI (`ai/ball-travel.ts`) after the owner
 * reported chasers arriving where the ball ISN'T. It is not AI code: it is a
 * description of what `ball.ts` already does to a rolling ball, and the STRIKE
 * path needs it for exactly the same reason the AI did — `buildPass`'s lead was
 * solved from `distance / launchSpeed`, i.e. as if the ball never slowed down.
 * A model of the sim's own physics belongs in `/sim`; `ai/ball-travel.ts` now
 * re-exports it so nothing about the AI's use of it changes.
 *
 * ── The finding it encodes ──────────────────────────────────────────────────
 *
 * `BALL_ROLLING_RESISTANCE` (0.02) says a grounded ball loses 0.2 m/s². That
 * number is real but it is almost never the one in force. While the ball is on
 * the turf, contact resolves EVERY tick with a normal speed of about g·dt, so
 * `applyContactFriction` fires every tick and takes a Coulomb bite out of the
 * tangential slip — roughly 30x rolling resistance. MEASURED through the real
 * pipeline (`ai/ball-travel.test.ts` pins these against a stepped ball):
 *
 *   launched at   travels in 1.5 s   speed at 1.5 s
 *        6 m/s          3.71 m           0.83 m/s
 *       10 m/s          7.28 m           1.66 m/s
 *       14 m/s         11.98 m           2.92 m/s
 *       18 m/s         17.06 m           5.18 m/s
 *       22 m/s         21.87 m           7.91 m/s
 *
 * A 10 m/s ground pass therefore travels about **9 m in total** and stops. Any
 * lead, intercept or pass weight computed as `v·t` — or from rolling resistance
 * — is wrong by many metres.
 *
 * `groundDecel(v)` is a two-branch fit to the deceleration the sim really
 * applies, sampled tick-by-tick off a 24 m/s roll:
 *
 *   v 23.6 → 12.52   v 15.0 → 9.30   v 10.4 → 8.56   v 4.0 → 7.00
 *   v  3.7 →  4.13   v  2.0 → 2.28   v  1.1 → 1.28
 *
 * There is a genuine DISCONTINUITY just under 4 m/s: above it the ball is
 * skidding and the Coulomb cap dominates (a base plus a v² aero term); below it
 * the ball has spun up enough that the slip — and so the friction — collapses,
 * and the decay becomes proportional to speed. The fit reproduces every sample
 * above to within ~9 %.
 *
 * ── Sim purity ──────────────────────────────────────────────────────────────
 *
 * Pure functions of their arguments. No RNG (so no draw-count change — recorded
 * replays are unaffected), no wall clock, no trig at all: only `+ - * /` and
 * `sqrt`, which are the correctly-rounded IEEE-754 ops, so this is bit-identical
 * on every engine for free (the same argument as `dpow025`, DECISIONS.md D2).
 * Results are returned through caller-supplied out-objects; nothing allocates.
 * (§15.7)
 *
 * ── What is DELIBERATELY still wrong ───────────────────────────────────────
 *
 * Magnus is not modelled and neither is the drag crisis. That is not an
 * oversight, it is §10.2: a curling or dipping ball must still wrong-foot the
 * AI exactly as it beats `goalkeeper.ts`'s own approximation. What this file
 * describes is the boring case — a ball rolling along the grass — which is what
 * a ground pass is.
 */

import {
  BALL_SKID_DECEL_BASE,
  BALL_SKID_DECEL_V2,
  BALL_SKID_LOW_SPEED,
  BALL_ROLL_DECAY_PER_S,
  BALL_TRAVEL_SUBSTEP_S,
  BALL_TRAVEL_MAX_HORIZON_S,
} from './constants.js';

/** Deceleration (m/s², positive) of a grounded ball moving at `v` m/s. */
export function groundDecel(v: number): number {
  if (v <= 0) return 0;
  if (v < BALL_SKID_LOW_SPEED) return BALL_ROLL_DECAY_PER_S * v;
  return BALL_SKID_DECEL_BASE + BALL_SKID_DECEL_V2 * v * v;
}

/** Result of a travel query. Reused; never allocated per call. (§15.7) */
export interface BallTravel {
  /** Distance covered along the ball's line (m). */
  dist: number;
  /** Speed at the end of the query (m/s). */
  speed: number;
  /** Seconds actually elapsed (may be < the asked-for t if the ball stopped). */
  time: number;
  /** True if the ball came to rest inside the query. */
  stopped: boolean;
  /**
   * travelForDistance only: did the ball actually GET there, inside
   * BALL_TRAVEL_MAX_HORIZON_S? False means it stopped short or was still short
   * of the target when the horizon ran out — either way, do not plan on it.
   */
  reached: boolean;
}

export function createBallTravel(): BallTravel {
  return { dist: 0, speed: 0, time: 0, stopped: false, reached: false };
}

/**
 * Integrate a grounded ball forward for `t` seconds. Semi-implicit Euler at
 * BALL_TRAVEL_SUBSTEP_S — the law is smooth inside each branch, so a 50 ms
 * substep tracks the 60 Hz sim to a few centimetres over a 2 s horizon
 * (ball-travel.test.ts pins the error).
 */
export function travelForTime(v0: number, t: number, out: BallTravel): void {
  let v = v0 > 0 ? v0 : 0;
  let d = 0;
  let elapsed = 0;
  const horizon = t < BALL_TRAVEL_MAX_HORIZON_S ? t : BALL_TRAVEL_MAX_HORIZON_S;
  while (elapsed < horizon && v > 0) {
    const h = horizon - elapsed < BALL_TRAVEL_SUBSTEP_S ? horizon - elapsed : BALL_TRAVEL_SUBSTEP_S;
    const vNext = v - groundDecel(v) * h;
    if (vNext <= 0) {
      // Stops inside this substep: it covers v²/(2a) more metres and quits.
      const a = groundDecel(v);
      d += a > 0 ? (v * v) / (2 * a) : 0;
      elapsed += a > 0 ? v / a : 0;
      v = 0;
      break;
    }
    d += (v + vNext) * 0.5 * h;
    v = vNext;
    elapsed += h;
  }
  out.dist = d;
  out.speed = v;
  out.time = elapsed;
  out.stopped = v <= 0;
  out.reached = false;
}

/**
 * How long a grounded ball launched at `v0` takes to travel `dist` metres, and
 * how fast it is going when it arrives. `out.stopped` = it never got there (and
 * `out.dist` is then the total distance it managed).
 */
export function travelForDistance(v0: number, dist: number, out: BallTravel): void {
  let v = v0 > 0 ? v0 : 0;
  let d = 0;
  let elapsed = 0;
  if (dist <= 0) {
    out.dist = 0;
    out.speed = v;
    out.time = 0;
    out.stopped = false;
    out.reached = true;
    return;
  }
  while (elapsed < BALL_TRAVEL_MAX_HORIZON_S && v > 0) {
    const h = BALL_TRAVEL_SUBSTEP_S;
    const vNext = v - groundDecel(v) * h;
    if (vNext <= 0) {
      const a = groundDecel(v);
      const remainder = a > 0 ? (v * v) / (2 * a) : 0;
      if (d + remainder >= dist) {
        // It just reaches: v_arrive = sqrt(v² − 2a(dist − d)).
        const need = dist - d;
        const sq = v * v - 2 * a * need;
        out.dist = dist;
        out.speed = sq > 0 ? Math.sqrt(sq) : 0;
        out.time = elapsed + (a > 0 ? (v - out.speed) / a : 0);
        out.stopped = false;
        out.reached = true;
        return;
      }
      out.dist = d + remainder;
      out.speed = 0;
      out.time = elapsed + (a > 0 ? v / a : 0);
      out.stopped = true;
      out.reached = false;
      return;
    }
    const step = (v + vNext) * 0.5 * h;
    if (d + step >= dist) {
      // Linear-interpolate inside the substep.
      const frac = step > 0 ? (dist - d) / step : 0;
      out.dist = dist;
      out.speed = v + (vNext - v) * frac;
      out.time = elapsed + h * frac;
      out.stopped = false;
      out.reached = true;
      return;
    }
    d += step;
    v = vNext;
    elapsed += h;
  }
  out.dist = d;
  out.speed = v;
  out.time = elapsed;
  out.stopped = v <= 0;
  out.reached = false; // horizon expired short of the target
}

/**
 * Total distance a grounded ball launched at `v0` covers before it comes to
 * rest — the RESTING POINT the anti-out test needs.
 *
 * The low-speed branch decays proportionally (dv/dt = −k·v), so it never
 * formally reaches zero; its remaining distance is exactly v/k in closed form.
 * Integrating the skid branch and then adding that tail gives the true asymptote
 * without an unbounded loop, and without pretending the ball stops dead the
 * moment the horizon expires.
 */
export function groundRunOut(v0: number, out: BallTravel): number {
  let v = v0 > 0 ? v0 : 0;
  let d = 0;
  let guard = 0;
  while (v >= BALL_SKID_LOW_SPEED && guard++ < 200) {
    const vNext = v - groundDecel(v) * BALL_TRAVEL_SUBSTEP_S;
    if (vNext <= BALL_SKID_LOW_SPEED) {
      // Finish the skid branch analytically down to the regime change.
      const a = groundDecel(v);
      if (a > 0) d += (v * v - BALL_SKID_LOW_SPEED * BALL_SKID_LOW_SPEED) / (2 * a);
      v = BALL_SKID_LOW_SPEED;
      break;
    }
    d += (v + vNext) * 0.5 * BALL_TRAVEL_SUBSTEP_S;
    v = vNext;
  }
  d += v / BALL_ROLL_DECAY_PER_S; // the exponential tail, in closed form
  out.dist = d;
  out.speed = 0;
  out.time = 0;
  out.stopped = true;
  out.reached = false;
  return d;
}
